import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { BookOpen, Trash2, Pencil } from 'lucide-react';
import { Turnstile } from '../components/ui/Turnstile';
import { getLibrary, listMyBooks, removeBook } from '../services/api';
import { useNavigate } from 'react-router-dom';

export const Library: React.FC = () => {
  const { user, requestMagic } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressItems, setProgressItems] = useState<any[]>([]);
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [myBooks, setMyBooks] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const [libRes, myRes] = await Promise.all([getLibrary(), listMyBooks()]);
        setProgressItems(libRes.progress || []);
        setBookmarks(libRes.bookmarks || []);
        setMyBooks(myRes.books || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-8 text-center animate-in fade-in">
        <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue mb-6">
          <BookOpen size={40} />
        </div>
        <h1 className="font-serif text-2xl font-bold mb-3">Your Library</h1>
        <p className="opacity-70 mb-8 max-w-xs">
          Sign in to access your reading progress, bookmarks, and saved books across all devices.
        </p>
        <form 
          className="w-full max-w-xs space-y-3" 
          onSubmit={async (e) => {
            e.preventDefault();
            if (!captchaToken) {
              alert('Please complete verification.');
              return;
            }
            setIsSending(true);
            try {
              await requestMagic(email, captchaToken);
              alert('Magic link sent! Check your email.');
            } catch (err) {
              alert((err as Error).message || 'Failed to send magic link.');
              setCaptchaToken('');
            } finally {
              setIsSending(false);
            }
          }}
        >
          <input 
            type="email"
            required
            placeholder="name@example.com"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-brand-blue outline-none transition-all"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Turnstile 
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || ''} 
            onVerify={(t) => setCaptchaToken(t)} 
            onError={() => setCaptchaToken('')}
          />
          <Button type="submit" className="w-full" disabled={isSending || !captchaToken}>
            {isSending ? 'Sending…' : 'Sign In with Magic Link'}
          </Button>
        </form>
        <p className="text-xs opacity-50 mt-3">No password required.</p>
      </div>
    );
  }

  return (
    <div className="pb-24 animate-in slide-in-from-bottom-4">
      {/* Hero */}
      <div className="relative overflow-hidden px-6 pt-8 pb-10 bg-gradient-to-r from-brand-blue/10 via-transparent to-brand-blue/5 border-b border-black/5 dark:border-white/5">
        <h1 className="font-serif text-3xl font-bold mb-2">My Archive</h1>
        <p className="opacity-70 max-w-xl text-sm">
          Your corner of the lab: saved cases, half-read files, and drafts you’re not ready to delete.
        </p>
        <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-brand-blue/10 to-transparent pointer-events-none" />
      </div>

      <div className="px-6 pt-6 space-y-10">
        {loading && <p className="text-sm opacity-60">Loading...</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}

        {/* Currently Reading */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-bold text-lg flex items-center gap-2">
              Reading Now
              <span className="text-xs font-normal bg-brand-blue text-white px-2 py-0.5 rounded-full">{progressItems.length}</span>
            </h2>
            <Link to="/search" className="text-xs text-brand-blue underline">Browse</Link>
          </div>
          <div className="space-y-4">
            {progressItems.map((item) => (
              <div key={item.book_id} className="bg-white dark:bg-white/5 p-4 rounded-xl shadow-sm border border-black/5 dark:border-white/5 flex gap-4">
                 <GeneratedCover title={item.title} author={item.author} size="sm" className="shrink-0 h-24 w-16" />
                 <div className="flex-1 flex flex-col justify-between py-1">
                    <div>
                      <h3 className="font-serif font-bold text-base leading-tight mb-1">{item.title}</h3>
                      <p className="text-xs opacity-60">{item.author}</p>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs opacity-70 mb-1.5">
                        <span>Chapter {item.chapter_id?.replace('ch-', '')}</span>
                        <span>{item.percentage}%</span>
                      </div>
                      <div className="w-full bg-black/5 dark:bg-white/10 h-2 rounded-full overflow-hidden">
                        <div className="bg-brand-blue h-full rounded-full" style={{ width: `${item.percentage}%` }} />
                      </div>
                      <Link to={`/book/${item.book_id}/read/${item.chapter_id}`} className="block text-right mt-3 text-brand-blue text-xs font-bold hover:underline">
                        CONTINUE READING
                      </Link>
                    </div>
                 </div>
              </div>
            ))}
            {progressItems.length === 0 && (
              <div className="text-sm opacity-60">
                <p>No active case files.</p>
                <p>A clear screen. For now.</p>
              </div>
            )}
          </div>
        </section>

        {/* Bookmarks */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-bold text-lg flex items-center gap-2">
              Bookmarks <span className="text-xs font-normal opacity-50">({bookmarks.length})</span>
            </h2>
            <Link to="/bookmarks" className="text-xs text-brand-blue underline">View All</Link>
          </div>
          <div className="space-y-3">
              {bookmarks.slice(0, 3).map((bookmark) => (
                 <div key={bookmark.id} className="p-4 rounded-xl bg-black/5 dark:bg.white/5 flex gap-4 group">
                     <div className="flex-1">
                         <h4 className="font-bold text-sm">{bookmark.title} <span className="font-normal opacity-60 text-xs">• {bookmark.chapter_id?.replace('ch-', 'Chapter ')}</span></h4>
                         <p className="text-sm font-serif italic opacity-70 my-2 line-clamp-2">"{bookmark.excerpt || 'No excerpt'}"</p>
                         <div className="flex justify-between items-center mt-2">
                            <span className="text-[10px] opacity-40">{bookmark.created_at}</span>
                            <Link to={`/book/${bookmark.book_id}/read/${bookmark.chapter_id}`} className="text-xs font-bold text-brand-blue">Go to location</Link>
                         </div>
                     </div>
                 </div>
              ))}
              {bookmarks.length === 0 && (
                <div className="text-sm opacity-60">
                  <p>You haven’t marked any evidence yet.</p>
                  <p>Once you start flagging lines, they’ll show up here.</p>
                </div>
              )}
          </div>
        </section>

        {/* Publishing CTA */}
        <section>
          <div className="p-5 rounded-2xl border border-dashed border-brand-blue/40 bg-brand-blue/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-brand-blue mb-1">Create</p>
              <h3 className="font-serif text-xl font-bold mb-2">Publish a New Case File</h3>
              <p className="text-sm opacity-70 max-w-md">Turn an idea, a memory, or a document trail into a story. Add chapters, choose what’s public, share it with anyone who wants to read.</p>
            </div>
            <Link to="/add-book">
              <Button className="gap-2">
                <BookOpen size={16} />
                Add Case File
              </Button>
            </Link>
          </div>
        </section>

        {/* My uploads */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans font-bold text-lg">My Releases</h2>
          </div>
          {myBooks.length === 0 && (
            <p className="text-sm opacity-60">
              You haven’t published any case files yet.<br />
              When you do, this is where they live.
            </p>
          )}
          <div className="space-y-3">
            {myBooks.map((book) => (
              <div key={book.id} className="p-4 rounded-xl border border-black/5 dark:border-white/10 bg-white/60 dark:bg-white/5 shadow-sm flex items-center gap-4">
                <GeneratedCover title={book.title} author={book.author} genre={book.genre} size="sm" className="shrink-0 h-20 w-14" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif font-bold text-base leading-tight mb-1">{book.title}</h3>
                  <p className="text-xs opacity-60">{book.genre}</p>
                  <p className="text-[11px] opacity-50">Published: {book.created_at || '—'}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => navigate(`/add-book?bookId=${book.id}`)}
                  >
                    <Pencil size={14} />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-500 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-900/20 gap-1"
                    onClick={async () => {
                      if (!window.confirm('Delete this book?')) return;
                      try {
                        await removeBook(book.id);
                        setMyBooks(prev => prev.filter(b => b.id !== book.id));
                      } catch (err) {
                        alert((err as Error).message);
                      }
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
